import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { parseDateOnly, formatDateOnly } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FileText, MapPin, CalendarClock, Rocket, Building2, Loader2, X, Check, Search,
  RefreshCw, ShieldAlert, ShieldCheck, Shield, Target, Landmark, GraduationCap, Globe, DollarSign, Layers,
} from "lucide-react";

const BUCKETS = [
  { key: "all", label: "All", icon: Layers },
  { key: "federal", label: "Federal", icon: Landmark },
  { key: "sled", label: "State / Local", icon: Building2 },
  { key: "grants", label: "Grants", icon: GraduationCap },
  { key: "other", label: "Other", icon: Globe },
];

const DATE_FILTERS = [
  { key: "all", label: "All dates" },
  { key: "today", label: "Due Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

const bondStyle = {
  required: { cls: "bg-red-50 text-red-700 border-red-200", Icon: ShieldAlert },
  partial: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Shield },
  none: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
  unknown: { cls: "bg-zinc-50 text-zinc-500 border-zinc-200", Icon: Shield },
};

function fmtVal(low, high) {
  const f = (v) => (v == null || v === "" ? null : `$${Number(v).toLocaleString()}`);
  const h = f(high), l = f(low);
  if (h && l && l !== h) return `${l} – ${h}`;
  return h || l || "—";
}

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const parseDue = (s) => { const d = parseDateOnly(s); return d ? startOfDay(d) : null; };

export default function Discover() {
  const navigate = useNavigate();
  const [buckets, setBuckets] = useState({ federal: [], sled: [], grants: [], other: [] });
  const [configured, setConfigured] = useState(true);
  const [tab, setTab] = useState("all");
  const [pastDueView, setPastDueView] = useState(false);          // dir 68 — default LIVE; toggle to Past-due
  const viewRef = useRef(false);
  const [meta, setMeta] = useState({ live_total: 0, past_due_total: 0, saved_total: 0, everything_total: 0, outside_saved_total: 0, passed_total: 0, verdict_epoch: null, swept_today: 0, as_of: "" });
  const [mode, setMode] = useState(() => (localStorage.getItem("ironclad-discover-mode") === "everything" ? "everything" : "saved"));  // saved | everything
  const modeRef = useRef(localStorage.getItem("ironclad-discover-mode") === "everything" ? "everything" : "saved");
  const [outsideOnly, setOutsideOnly] = useState(false);   // Dir 141 — nudge: show only opps outside the saved search
  const outsideRef = useRef(false);
  const [offset, setOffset] = useState(0);                 // Dir 141 — server-side pagination cursor
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const loadingMoreRef = useRef(false);   // synchronous lock so the button + scroll sentinel can't double-fetch
  const PAGE = 200;
  const [naics, setNaics] = useState("");
  const [keyword, setKeyword] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState("");
  const [brief, setBrief] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pending, setPending] = useState(null);   // Dir 139 — buffered refresh when user is scrolled down
  const [fitCfg, setFitCfg] = useState(null);  // Phase D — bonding capacity for the fit chip
  const seenAtRef = useRef(null); // this user's last_seen_discover BEFORE this visit (for NEW tags)
  // Dir 183 — verdict filter + sort, feed-health banner, and the Passed log (auditable + reversible).
  const [verdictFilter, setVerdictFilter] = useState("all");   // all | GO | NEEDS-REVIEW | NO-GO
  const [pursuableFirst, setPursuableFirst] = useState(false); // sort GO -> NEEDS-REVIEW -> NO-GO
  const [health, setHealth] = useState(null);                  // { go, review, nogo, total }
  const [passedView, setPassedView] = useState(false);
  const [passedRows, setPassedRows] = useState([]);
  const [passedLoading, setPassedLoading] = useState(false);

  const _metaFrom = (d) => ({
    live_total: d.live_total || 0, past_due_total: d.past_due_total || 0,
    saved_total: d.saved_total || 0, everything_total: d.everything_total || 0,
    outside_saved_total: d.outside_saved_total || 0, total: d.total || 0, passed_total: d.passed_total || 0,
    verdict_epoch: d.verdict_epoch || null,
    swept_today: d.swept_today || 0, as_of: d.as_of || "",
  });
  const _emptyBuckets = () => ({ federal: [], sled: [], grants: [], other: [] });
  const _mergeBuckets = (prev, next) => {
    const out = {};
    for (const k of ["federal", "sled", "grants", "other"]) {
      const seen = new Set((prev[k] || []).map((x) => x.opp_key));
      out[k] = [...(prev[k] || []), ...((next[k] || []).filter((x) => !seen.has(x.opp_key)))];
    }
    return out;
  };
  const _qs = (off) => {
    const view = viewRef.current ? "past_due" : "live";
    const p = new URLSearchParams({ view, mode: modeRef.current, offset: String(off), limit: String(PAGE) });
    if (outsideRef.current) p.set("outside_saved", "true");
    return `/opportunities?${p.toString()}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(_qs(0));
      setBuckets(r.data.buckets || _emptyBuckets());
      setConfigured(r.data.configured !== false);
      setMeta(_metaFrom(r.data));
      const off = r.data.returned || 0;
      offsetRef.current = off; setOffset(off); setHasMore(!!r.data.has_more);
      setLastUpdated(new Date());
      setPending(null);
      loadHealth();   // Dir 183 — refresh the feed-health banner totals (off the 60s silent tick)
    } catch (e) {
      toast.error("Couldn't load the opportunity feed.");
    } finally {
      setLoading(false);
    }
  };

  // Dir 141 — server-side pagination. Fetch the next page and APPEND (dedupe by opp_key) so the full feed —
  // including the everything-only tail — is reachable past the old 400-row cap. Never moves the scroll (Dir 139).
  const loadMore = async () => {
    if (loadingMoreRef.current || !hasMore || loading) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const r = await api.get(_qs(offsetRef.current));
      setBuckets((prev) => _mergeBuckets(prev, r.data.buckets || _emptyBuckets()));
      const off = offsetRef.current + (r.data.returned || 0);
      offsetRef.current = off; setOffset(off); setHasMore(!!r.data.has_more);
    } catch { /* silent */ }
    finally { loadingMoreRef.current = false; setLoadingMore(false); }
  };

  // Dir 139 — SILENT auto-refresh: never toggles the loading spinner, never moves the user. Only auto-applies
  // page 0 when the user is unscrolled AND hasn't paged down; otherwise buffer a non-intrusive "N new" pill.
  const silentRefresh = async () => {
    if (document.querySelector('[data-testid="brief-modal"]')) return;   // don't disrupt an open brief
    try {
      const r = await api.get(_qs(0));
      const bk = r.data.buckets || _emptyBuckets();
      const mt = _metaFrom(r.data);
      const atTop = window.scrollY < 200 && offsetRef.current <= PAGE;
      if (atTop) {
        setBuckets(bk); setMeta(mt);
        offsetRef.current = r.data.returned || 0; setOffset(offsetRef.current); setHasMore(!!r.data.has_more);
        setLastUpdated(new Date()); setPending(null);
      } else {
        const delta = (mt.live_total || 0) - (meta.live_total || 0);
        setPending({ delta: delta > 0 ? delta : 0 });
      }
    } catch { /* silent — never toast on background tick */ }
  };

  const applyPending = () => {
    setPending(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    load();
  };

  // Dir 141 — the "outside your saved search" nudge: show ONLY the net-new opportunities Saved mode filters out.
  const showOutside = () => { outsideRef.current = true; setOutsideOnly(true); load(); };
  const clearOutside = () => { outsideRef.current = false; setOutsideOnly(false); load(); };

  // dir 68 — flip between the default LIVE feed and the dedicated Past-due (reference/history) view.
  const toggleView = (pd) => { setPassedView(false); viewRef.current = pd; setPastDueView(pd); load(); };

  // Dir 183 — feed-health banner totals (GO / NEEDS-REVIEW / NO-GO across the current view). Fetched on load
  // and manual refresh only; the heavier full-view scan is a dedicated endpoint kept off the 60s silent tick.
  const loadHealth = async () => {
    if (viewRef.current) { setHealth(null); return; }   // past-due view has no "today" verdict banner
    try {
      const p = new URLSearchParams({ view: "live", mode: modeRef.current });
      if (outsideRef.current) p.set("outside_saved", "true");
      const r = await api.get(`/opportunities/verdict-summary?${p.toString()}`);
      setHealth(r.data);
    } catch { setHealth(null); }
  };

  // Dir 183 — the Passed LOG: auditable (reason + who/when) and reversible (un-pass returns it to the feed).
  const loadPassed = async () => {
    setPassedLoading(true);
    try {
      const r = await api.get("/opportunities/passed");
      setPassedRows(r.data.passed || []);
      setMeta((m) => ({ ...m, passed_total: r.data.total || 0 }));
    } catch { toast.error("Couldn't load passed opportunities."); }
    finally { setPassedLoading(false); }
  };

  const showPassed = () => { setPassedView(true); setPastDueView(false); viewRef.current = false; loadPassed(); };

  const unpass = async (o) => {
    setBusy(o.opp_key);
    try {
      await api.post(`/opportunities/${o.opp_key}/unpass`);
      setPassedRows((rows) => rows.filter((x) => x.opp_key !== o.opp_key));
      setMeta((m) => ({ ...m, passed_total: Math.max(0, (m.passed_total || 1) - 1) }));
      toast.success("Un-passed — back in the live feed.");
    } catch { toast.error("Couldn't un-pass that one."); }
    finally { setBusy(""); }
  };

  // Capture the prior last-seen (to highlight NEW cards), load the feed, then mark Discover seen
  // for THIS user and clear their nav badge. Auto-refresh the feed every 60s.
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/opportunities/new-count"); seenAtRef.current = r.data.last_seen || null; }
      catch { /* ignore */ }
      try { const c = await api.get("/bid-fit/config"); setFitCfg(c.data); } catch { /* ignore */ }
      await load();
      try { await api.post("/opportunities/seen"); window.dispatchEvent(new Event("ironclad:discover-seen")); }
      catch { /* ignore */ }
    })();
    const id = setInterval(silentRefresh, 60000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/opportunities/refresh", { mode, naics: naics.trim() });
      toast.success(`Pulled ${r.data.pulled} from HigherGov · ${r.data.new} new.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "HigherGov pull failed.");
    } finally {
      setRefreshing(false);
    }
  };

  const switchMode = (m) => {
    if (m === modeRef.current && !outsideRef.current) return;
    modeRef.current = m;
    setMode(m);
    localStorage.setItem("ironclad-discover-mode", m);
    outsideRef.current = false; setOutsideOnly(false);   // leaving the "outside" nudge view
    load();   // Dir 140 — re-query the read path so the displayed list changes immediately (no manual refresh)
  };

  const briefMe = async (o) => {
    setBrief({ opp: o, text: "", facts: null, loading: true, accepting: false });
    try {
      const r = await api.post("/opportunities/brief", { opportunity: o });
      setBrief({ opp: o, text: r.data.brief, facts: r.data.facts || null, loading: false, accepting: false });
    } catch (e) {
      setBrief({ opp: o, text: e?.response?.data?.detail || "Anvil couldn't brief this one.", facts: null, loading: false, accepting: false });
    }
  };

  const pursue = async (o) => {
    const opp = o || brief?.opp;
    if (!opp) return;
    if (brief) setBrief((b) => ({ ...b, accepting: true }));
    setBusy(opp.opp_key);
    try {
      const r = await api.post(`/opportunities/${opp.opp_key}/pursue`);
      const n = r.data.documents_attached;
      toast.success(n ? `Pursuing — pulled ${n} solicitation document(s). Let Anvil auto-run.` : "Pursuing — added to the pipeline.");
      setBrief(null);
      navigate(`/deals/${r.data.deal_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to pursue this opportunity.");
      if (brief) setBrief((b) => ({ ...b, accepting: false }));
    } finally {
      setBusy("");
    }
  };

  const removeFromBuckets = (opp_key) =>
    setBuckets((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v.filter((x) => x.opp_key !== opp_key)])));

  // Dir 184 — which bucket an opp currently sits in (so Undo restores it to the right tab).
  const findBucket = (opp_key) => {
    for (const [k, v] of Object.entries(buckets)) if ((v || []).some((x) => x.opp_key === opp_key)) return k;
    return "other";
  };

  // Dir 184 — one-tap Undo for a just-passed opportunity: un-pass and drop it straight back into its bucket,
  // no need to open the Passed tab. Fires from the "Undo" action on the post-pass toast.
  const undoPass = async (o, bucketKey) => {
    try {
      await api.post(`/opportunities/${o.opp_key}/unpass`);
      setBuckets((prev) => {
        const k = bucketKey && prev[bucketKey] ? bucketKey : "other";
        const list = (prev[k] || []).filter((x) => x.opp_key !== o.opp_key);
        return { ...prev, [k]: [o, ...list] };
      });
      setMeta((m) => ({ ...m, passed_total: Math.max(0, (m.passed_total || 1) - 1) }));
      toast.success("Restored to the feed.");
    } catch { toast.error("Couldn't undo that."); }
  };

  // Dir 182 — Pass directly from the brief: a clear NO-GO never needs a Pursue-then-decline detour. Logs the
  // brief's no-bid reason (recommendation + showstoppers) so the decision is auditable.
  const passFromBrief = async () => {
    const opp = brief?.opp;
    if (!opp) return;
    const f = brief?.facts;
    const reason = (f
      ? `No-bid from brief (${f.recommendation})` + (f.showstoppers?.length ? `: ${f.showstoppers.join("; ")}` : "")
      : "Passed from Anvil brief").slice(0, 800);
    setBrief((b) => ({ ...b, accepting: true }));
    setBusy(opp.opp_key);
    const bucketKey = findBucket(opp.opp_key);
    try {
      await api.post(`/opportunities/${opp.opp_key}/pass`, { reason });
      removeFromBuckets(opp.opp_key);
      setMeta((m) => ({ ...m, passed_total: (m.passed_total || 0) + 1 }));
      setBrief(null);
      toast("Passed — logged the no-bid reason.", { duration: 6000, action: { label: "Undo", onClick: () => undoPass(opp, bucketKey) } });
    } catch (e) {
      toast.error("Couldn't pass that one.");
      setBrief((b) => ({ ...b, accepting: false }));
    } finally {
      setBusy("");
    }
  };

  const pass = async (o) => {
    setBusy(o.opp_key);
    const bucketKey = findBucket(o.opp_key);
    try {
      await api.post(`/opportunities/${o.opp_key}/pass`, { reason: "Passed from Discover" });
      removeFromBuckets(o.opp_key);
      setMeta((m) => ({ ...m, passed_total: (m.passed_total || 0) + 1 }));
      toast("Passed. It won't show in the feed.", { duration: 6000, action: { label: "Undo", onClick: () => undoPass(o, bucketKey) } });
    } catch (e) {
      toast.error("Couldn't pass that one.");
    } finally {
      setBusy("");
    }
  };

  // counts per bucket (pre date/keyword filters)
  const counts = useMemo(() => {
    const all = [...(buckets.federal || []), ...(buckets.sled || []), ...(buckets.grants || []), ...(buckets.other || [])];
    return { all: all.length, federal: (buckets.federal || []).length, sled: (buckets.sled || []).length, grants: (buckets.grants || []).length, other: (buckets.other || []).length };
  }, [buckets]);

  const filtered = useMemo(() => {
    let rows = tab === "all"
      ? [...(buckets.federal || []), ...(buckets.sled || []), ...(buckets.grants || []), ...(buckets.other || [])]
      : (buckets[tab] || []);

    const kw = keyword.trim().toLowerCase();
    if (kw) {
      rows = rows.filter((o) => [o.title, o.agency_name, o.source_id, o.ai_summary, o.naics_code, o.set_aside]
        .filter(Boolean).join(" ").toLowerCase().includes(kw));
    }

    const today = startOfDay(new Date());
    rows = rows.filter((o) => {
      const due = parseDue(o.due_date);
      if (dateFilter === "all" && !range.from && !range.to) return true;
      if (!due) return false;
      if (range.from && due < startOfDay(range.from)) return false;
      if (range.to && due > startOfDay(range.to)) return false;
      if (dateFilter === "today") return due.getTime() === today.getTime();
      if (dateFilter === "week") { const end = new Date(today); end.setDate(end.getDate() + 7); return due >= today && due <= end; }
      if (dateFilter === "month") { const end = new Date(today); end.setDate(end.getDate() + 30); return due >= today && due <= end; }
      return true;
    });

    // Dir 183 — verdict filter: jump straight to just GO / NEEDS-REVIEW / NO-GO and skip the noise.
    if (verdictFilter !== "all") {
      rows = rows.filter((o) => (o.brief_verdict?.recommendation || "") === verdictFilter);
    }

    // Sort: "Pursuable first" ranks GO -> NEEDS-REVIEW -> NO-GO; otherwise soonest due first (no-due last).
    const rank = { "GO": 0, "NEEDS-REVIEW": 1, "NO-GO": 2 };
    return rows.slice().sort((a, b) => {
      if (pursuableFirst) {
        const ra = rank[a.brief_verdict?.recommendation] ?? 3;
        const rb = rank[b.brief_verdict?.recommendation] ?? 3;
        if (ra !== rb) return ra - rb;
      }
      const da = parseDue(a.due_date), db_ = parseDue(b.due_date);
      if (da && db_) return da - db_;
      if (da) return -1;
      if (db_) return 1;
      return 0;
    });
  }, [buckets, tab, keyword, dateFilter, range, verdictFilter, pursuableFirst]);

  const sentinelRef = useRef(null);
  // Dir 141 — infinite scroll: when the bottom sentinel enters view, fetch the next server page.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
    /* eslint-disable-next-line */
  }, [hasMore, loadingMore, loading, offset]);

  // Dir 183 — when a verdict filter is active, drain the remaining pages so "just GO" (etc.) is COMPLETE across
  // the whole feed, not just the first loaded page. Verdicts already ride on each row, so this only paginates.
  useEffect(() => {
    if (verdictFilter !== "all" && hasMore && !loadingMore && !loading && !pastDueView && !passedView) {
      loadMore();
    }
    /* eslint-disable-next-line */
  }, [verdictFilter, hasMore, loadingMore, loading]);

  return (
    <div className="p-5 sm:p-8 max-w-[1600px] mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-zinc-900">Discover</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Stage 1 intake. One feed surfaces federal, state/local and grant solicitations in WD's lanes.
            Brief one with Anvil, then <strong>Pursue</strong> — it pulls the full solicitation docs and opens the cockpit.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Button data-testid="opp-refresh-button" onClick={refresh} disabled={refreshing || !configured}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm">
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {refreshing ? "Pulling…" : "Refresh"}
          </Button>
          {lastUpdated && (
            <span data-testid="last-updated" className="text-[11px] text-zinc-400 inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · auto-refreshing
            </span>
          )}
        </div>
      </div>

      {!configured && (
        <p className="text-[12px] text-amber-700 mt-3 font-mono bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
          HigherGov isn't configured. Add the API key + Saved Search in Settings to populate the feed.
        </p>
      )}

      {/* Dir 183 — feed-health banner: today's verdict totals, one line, clickable to filter the feed. */}
      {!passedView && health && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="feed-health-banner">
          <span className="text-[11px] uppercase tracking-wide text-zinc-400 mr-1">Today's feed</span>
          <button data-testid="health-go" onClick={() => setVerdictFilter((v) => (v === "GO" ? "all" : "GO"))}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${verdictFilter === "GO" ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" /> {health.go} GO
          </button>
          <span className="text-zinc-300">·</span>
          <button data-testid="health-review" onClick={() => setVerdictFilter((v) => (v === "NEEDS-REVIEW" ? "all" : "NEEDS-REVIEW"))}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${verdictFilter === "NEEDS-REVIEW" ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" /> {health.review} review
          </button>
          <span className="text-zinc-300">·</span>
          <button data-testid="health-nogo" onClick={() => setVerdictFilter((v) => (v === "NO-GO" ? "all" : "NO-GO"))}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${verdictFilter === "NO-GO" ? "bg-red-600 text-white border-red-600" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" /> {health.nogo} no-go
          </button>
          <span className="text-xs text-zinc-400">today{health.capped ? " (sample)" : ""}</span>
        </div>
      )}

      {/* Mode toggle + filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden" data-testid="opp-mode-toggle">
          <button data-testid="opp-mode-saved" onClick={() => switchMode("saved")}
            title="Opportunities matching WD's saved-search criteria (configured NAICS lanes / saved HigherGov search)."
            className={`px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${mode === "saved" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Saved Search
            <span data-testid="opp-mode-saved-count" className={`text-[10px] px-1.5 py-0.5 rounded-full ${mode === "saved" ? "bg-white/25" : "bg-zinc-100 text-zinc-500"}`}>{meta.saved_total}</span>
          </button>
          <button data-testid="opp-mode-everything" onClick={() => switchMode("everything")}
            title="The full unfiltered feed — every opportunity pulled, across all lanes."
            className={`px-3 py-2 text-sm font-medium border-l border-zinc-200 transition-colors inline-flex items-center gap-1.5 ${mode === "everything" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Everything
            <span data-testid="opp-mode-everything-count" className={`text-[10px] px-1.5 py-0.5 rounded-full ${mode === "everything" ? "bg-white/25" : "bg-zinc-100 text-zinc-500"}`}>{meta.everything_total}</span>
          </button>
        </div>

        <div className="relative w-40">
          <Input data-testid="naics-input" value={naics} onChange={(e) => setNaics(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && refresh()} placeholder="NAICS (236220)" className="rounded-sm font-mono text-sm" />
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input data-testid="keyword-input" value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Search title, agency, keyword…" className="pl-9 rounded-sm text-sm" />
        </div>
      </div>

      {/* Date quick filters + custom range (live feed only) */}
      {!pastDueView && !passedView && (
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="opp-date-filters">
        {DATE_FILTERS.map((d) => (
          <button key={d.key} data-testid={`date-filter-${d.key}`} onClick={() => setDateFilter(d.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${dateFilter === d.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>
            {d.label}
          </button>
        ))}
        <span className="text-zinc-300">|</span>
        <input type="date" data-testid="range-from" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          className="text-xs border border-zinc-200 rounded-sm px-2 py-1.5 text-zinc-600" />
        <span className="text-xs text-zinc-400">to</span>
        <input type="date" data-testid="range-to" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          className="text-xs border border-zinc-200 rounded-sm px-2 py-1.5 text-zinc-600" />
        {(range.from || range.to) && (
          <button onClick={() => setRange({ from: "", to: "" })} className="text-xs text-zinc-400 hover:text-zinc-700">clear</button>
        )}
        <span className="text-zinc-300">|</span>
        <button data-testid="sort-pursuable" onClick={() => setPursuableFirst((v) => !v)}
          title="Sort pursuable opportunities first: GO, then NEEDS-REVIEW, then NO-GO."
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${pursuableFirst ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>
          <Rocket className="h-3 w-3" /> Pursuable first
        </button>
        {verdictFilter !== "all" && (
          <button data-testid="verdict-clear" onClick={() => setVerdictFilter("all")}
            className="text-xs text-zinc-500 hover:text-zinc-800 underline">Showing {verdictFilter} only — clear</button>
        )}
      </div>
      )}

      {/* Bucket tabs + LIVE / Past-due view toggle (dir 68) */}
      <div className="mt-5 flex items-center gap-1.5 border-b border-zinc-200 overflow-x-auto" data-testid="opp-buckets">
        {BUCKETS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button key={key} data-testid={`opp-tab-${key}`} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                active ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
              <Icon className="h-4 w-4" /> {label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500"}`}>{counts[key]}</span>
            </button>
          );
        })}
        <div className="ml-auto flex items-center pl-2 shrink-0">
          <button
            data-testid="opp-view-live"
            onClick={() => toggleView(false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              (!pastDueView && !passedView) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300"}`}>
            Live <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${(!pastDueView && !passedView) ? "bg-white/25" : "bg-zinc-100 text-zinc-500"}`}>{meta.live_total}</span>
          </button>
          <button
            data-testid="opp-view-past-due"
            onClick={() => toggleView(true)}
            title="Expired opportunities — reference/history only. Kept out of the live feed."
            className={`ml-1.5 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              pastDueView ? "bg-red-600 text-white border-red-600" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300"}`}>
            Past due <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${pastDueView ? "bg-white/25" : "bg-red-50 text-red-600"}`}>{meta.past_due_total}</span>
          </button>
          <button
            data-testid="opp-view-passed"
            onClick={showPassed}
            title="Opportunities you passed — auditable (with the logged no-bid reason) and reversible."
            className={`ml-1.5 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              passedView ? "bg-zinc-800 text-white border-zinc-800" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300"}`}>
            Passed <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${passedView ? "bg-white/25" : "bg-zinc-100 text-zinc-500"}`}>{meta.passed_total}</span>
          </button>
        </div>
      </div>

      {!pastDueView && !passedView && meta.swept_today > 0 && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-400" data-testid="auto-swept-line">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Feed kept current — {meta.swept_today} past-due {meta.swept_today === 1 ? "opportunity" : "opportunities"} auto-swept today; view them under <b className="text-zinc-500">Past due</b>.
        </div>
      )}

      {passedView && (
        <div className="mt-3 text-[12px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2" data-testid="passed-notice">
          <b>Passed opportunities</b> — the auditable no-bid log. Each shows the logged reason and who/when. Un-pass any to send it straight back to the live feed.
        </div>
      )}

      {pastDueView && (
        <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="past-due-notice">
          Showing <b>expired</b> opportunities (response date already passed) for reference/history. These are kept out of the live feed — WD can no longer bid them.
        </div>
      )}

      {/* Dir 141 — "outside your saved search" nudge: surface the net-new opportunities Saved mode filters out */}
      {!outsideOnly && !pastDueView && !passedView && meta.outside_saved_total > 0 && (
        <div className="mt-4 flex justify-center" data-testid="opp-outside-nudge">
          <button onClick={showOutside} data-testid="opp-outside-view"
            className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full border border-gold-dark/40 bg-gold-dark/10 text-zinc-800 hover:bg-gold-dark/20 transition-colors">
            <Target className="h-3.5 w-3.5 text-gold-dark" />
            {meta.outside_saved_total} {meta.outside_saved_total === 1 ? "opportunity" : "opportunities"} outside your saved search — view
          </button>
        </div>
      )}
      {outsideOnly && (
        <div className="mt-4 flex items-center justify-center gap-3 text-[12px] text-zinc-600" data-testid="opp-outside-active">
          <span className="inline-flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-gold-dark" /> Showing opportunities <b>outside</b> your saved search ({meta.outside_saved_total})</span>
          <button onClick={clearOutside} data-testid="opp-outside-clear" className="text-zinc-400 hover:text-zinc-800 underline">clear</button>
        </div>
      )}

      {!passedView && pending && pending.delta > 0 && (
        <div className="mt-4 flex justify-center" data-testid="opp-pending-banner">
          <button onClick={applyPending} data-testid="opp-pending-apply"
            className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full bg-zinc-900 text-white shadow-sm hover:bg-zinc-800 transition-colors">
            <RefreshCw className="h-3.5 w-3.5" /> {pending.delta} new {pending.delta === 1 ? "opportunity" : "opportunities"} — show
          </button>
        </div>
      )}

      {!passedView && (
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="opportunities-list">
        {loading && <div className="text-sm text-zinc-400 font-mono flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading feed…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-sm text-zinc-400 font-mono col-span-full py-10 text-center border border-dashed border-zinc-200 rounded-sm">
            No opportunities match. Try Refresh, switch to "Everything", or clear the date filters.
          </div>
        )}
        {!loading && filtered.map((o) => {
          const bond = bondStyle[o.bonds?.level] || bondStyle.unknown;
          const BondIcon = bond.Icon;
          // dir 68 — past-due boundary uses WD's Chicago calendar date from the server (string compare, tz-safe).
          const dueStr = o.due_date ? String(o.due_date).slice(0, 10) : "";
          const overdue = dueStr && meta.as_of && dueStr < meta.as_of;
          const dueToday = dueStr && meta.as_of && dueStr === meta.as_of;
          const isNew = !overdue && o.ingested_at && (!seenAtRef.current || o.ingested_at > seenAtRef.current);
          // Dir 185 — this row's verdict predates the last bid-fit profile change -> flag it as possibly stale.
          const staleVerdict = meta.verdict_epoch && o.brief_verdict?.graded_at &&
            new Date(o.brief_verdict.graded_at) < new Date(meta.verdict_epoch);
          return (
            <div key={o.opp_key} data-testid={`opp-${o.opp_key}`}
              className={`bg-white border rounded-sm p-5 transition-colors flex flex-col ${overdue ? "border-red-300" : dueToday ? "border-amber-300" : "border-zinc-200 hover:border-zinc-300"}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display font-semibold text-base text-zinc-900 leading-snug">{o.title}</h3>
                <div className="flex items-center gap-1.5 shrink-0">
                  {o.brief_verdict?.recommendation && (
                    <span data-testid={`opp-verdict-${o.opp_key}`}
                      title={o.brief_verdict.reason || `Brief verdict: ${o.brief_verdict.recommendation}`}
                      className={`text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-sm whitespace-nowrap ${
                        o.brief_verdict.recommendation === "GO" ? "bg-emerald-100 text-emerald-700"
                        : o.brief_verdict.recommendation === "NO-GO" ? "bg-red-100 text-red-700"
                        : "bg-amber-100 text-amber-700"}`}>
                      {o.brief_verdict.recommendation}
                    </span>
                  )}
                  {isNew && (
                    <span data-testid={`opp-new-${o.opp_key}`}
                      className="text-[10px] font-bold tracking-wide px-2 py-0.5 bg-gold/20 text-amber-800 rounded-sm whitespace-nowrap">NEW</span>
                  )}
                  {o.set_aside && (
                    <span className="text-[10px] tracking-wide px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded-sm whitespace-nowrap">{o.set_aside}</span>
                  )}
                </div>
              </div>
              <div className="mt-1.5 font-mono text-xs text-zinc-500">{o.source_id || o.opp_key}</div>
              {staleVerdict && (
                <div data-testid={`opp-verdict-stale-${o.opp_key}`}
                  title="Your company profile changed after this was graded — re-run the brief for a current, doc-aware verdict."
                  className="mt-1 flex items-center gap-1 text-[10px] text-amber-600">
                  <RefreshCw className="h-3 w-3 shrink-0" /> graded at ingest · re-run brief to refresh
                </div>
              )}

              <div className="mt-3 space-y-1.5 text-sm text-zinc-600">
                <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-zinc-400 shrink-0" /> {o.agency_name || "—"}</div>
                <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-zinc-400 shrink-0" /> {[o.pop_city, o.pop_state].filter(Boolean).join(", ") || "—"} · NAICS {o.naics_code || "—"}</div>
                <div className="flex items-center gap-2"><DollarSign className="h-3.5 w-3.5 text-zinc-400 shrink-0" /> {fmtVal(o.val_est_low, o.val_est_high)}</div>
                <div className={`flex items-center gap-2 ${overdue ? "text-red-600 font-medium" : dueToday ? "text-amber-700 font-medium" : ""}`}>
                  <CalendarClock className="h-3.5 w-3.5 shrink-0" /> Due {o.due_date ? formatDateOnly(o.due_date) : "—"}
                  {overdue && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-sm">PAST DUE</span>}
                  {dueToday && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">TODAY</span>}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                <span data-testid={`opp-notice-${o.opp_key}`} title="Notice type (from HigherGov)"
                  className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-sm ${(o.biddable !== false) ? "bg-blue-600 text-white" : "bg-amber-500 text-white"}`}>
                  <FileText className="h-3 w-3" /> {o.notice_class || "Solicitation"}
                </span>
                <span title={o.bonds?.note} className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border ${bond.cls}`}>
                  <BondIcon className="h-3 w-3" /> Bonds: {o.bonds?.level || "unknown"}
                </span>
                <span title={o.naics_match?.note}
                  className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border ${o.naics_match?.match ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>
                  <Target className="h-3 w-3" /> {o.naics_match?.match ? "WD lane" : "Review fit"}
                </span>
                {(() => {
                  // Phase D — envelope fit chip: high end of the estimated band vs WD's single-job bond limit.
                  const lim = fitCfg?.single_job_limit;
                  const est = o.val_est_high || o.val_est_low;
                  if (!lim || !est) return null;
                  const over = est > lim;
                  return (
                    <span data-testid={`opp-fit-${o.opp_key}`}
                      title={over ? `Est. up to $${Number(est).toLocaleString()} exceeds WD single-job bond limit $${Number(lim).toLocaleString()}` : `Within WD single-job bond limit $${Number(lim).toLocaleString()}`}
                      className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border ${over ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                      <Target className="h-3 w-3" /> {over ? "Outside envelope" : "In envelope"}
                    </span>
                  );
                })()}
                {o.set_aside_note && (
                  <span title={o.set_aside_note} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border bg-zinc-50 text-zinc-500 border-zinc-200">
                    <FileText className="h-3 w-3" /> Set-aside
                  </span>
                )}
              </div>

              {o.ai_summary && (
                <p className="mt-3 text-xs text-zinc-500 leading-relaxed line-clamp-3">{o.ai_summary}</p>
              )}

              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center gap-2">
                <Button data-testid={`opp-brief-${o.opp_key}`} onClick={() => briefMe(o)}
                  className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm">
                  <AnvilIcon className="h-4 w-4 mr-2" /> Brief me
                </Button>
                <Button data-testid={`opp-pursue-${o.opp_key}`} onClick={() => pursue(o)} disabled={busy === o.opp_key}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm">
                  {busy === o.opp_key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                </Button>
                <button data-testid={`opp-pass-${o.opp_key}`} onClick={() => pass(o)} disabled={busy === o.opp_key}
                  className="text-xs px-3 py-2 rounded-sm bg-zinc-100 hover:bg-zinc-200 text-zinc-600 disabled:opacity-50">Pass</button>
              </div>
            </div>
          );
        })}
        {!loading && hasMore && (
          <div ref={sentinelRef} data-testid="opps-more" className="col-span-full py-4 flex flex-col items-center gap-2">
            <button onClick={loadMore} disabled={loadingMore} data-testid="opps-load-more"
              className="text-xs font-medium px-4 py-2 rounded-full border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 disabled:opacity-50 inline-flex items-center gap-2">
              {loadingMore ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</> : `Load more (${filtered.length} of ${meta.total})`}
            </button>
          </div>
        )}
      </div>
      )}

      {/* Dir 183 — Passed log: auditable no-bid reasons + one-click un-pass (reversible). */}
      {passedView && (
        <div className="mt-6 space-y-2" data-testid="passed-list">
          {passedLoading && <div className="text-sm text-zinc-400 font-mono flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading passed…</div>}
          {!passedLoading && passedRows.length === 0 && (
            <div className="text-sm text-zinc-400 font-mono py-10 text-center border border-dashed border-zinc-200 rounded-sm">
              Nothing passed yet. When you Pass an opportunity, it lands here with its logged no-bid reason.
            </div>
          )}
          {!passedLoading && passedRows.map((o) => (
            <div key={o.opp_key} data-testid={`passed-${o.opp_key}`} className="bg-white border border-zinc-200 rounded-sm p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm text-zinc-900 leading-snug">{o.title}</div>
                <div className="mt-0.5 font-mono text-[11px] text-zinc-500">{o.source_id || o.opp_key} · {o.agency_name || o.agency || "—"} · NAICS {o.naics_code || "—"}</div>
                {o.pass_reason && (
                  <div className="mt-2 text-xs text-zinc-600 bg-zinc-50 border border-zinc-100 rounded-sm px-2.5 py-1.5" data-testid={`passed-reason-${o.opp_key}`}>
                    <span className="font-semibold text-zinc-500">No-bid reason: </span>{o.pass_reason}
                  </div>
                )}
                <div className="mt-1.5 text-[10px] text-zinc-400">
                  Passed{o.decided_by ? ` by ${o.decided_by}` : ""}{o.decided_at ? ` · ${new Date(o.decided_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
                </div>
              </div>
              <button data-testid={`unpass-${o.opp_key}`} onClick={() => unpass(o)} disabled={busy === o.opp_key}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                {busy === o.opp_key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Un-pass
              </button>
            </div>
          ))}
        </div>
      )}

      {brief && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" data-testid="brief-modal" onClick={() => !brief.accepting && setBrief(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 bg-zinc-950 text-white rounded-t-xl">
              <div className="flex items-center gap-2.5">
                <span className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center"><AnvilIcon className="h-5 w-5 text-gold" /></span>
                <div className="leading-tight">
                  <div className="font-display font-bold">Anvil's brief</div>
                  <div className="text-[10px] text-zinc-400 font-mono">{brief.opp.source_id || brief.opp.opp_key}</div>
                </div>
              </div>
              <button onClick={() => !brief.accepting && setBrief(null)} data-testid="brief-close"><X className="h-5 w-5 text-zinc-400 hover:text-white" /></button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              <h3 className="font-semibold text-zinc-900 text-sm mb-3">{brief.opp.title}</h3>
              {brief.loading ? (
                <div className="flex items-center gap-2 text-sm text-zinc-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Anvil is pulling and reading the solicitation documents…</div>
              ) : (
                <div className="space-y-4" data-testid="brief-body">
                  {brief.facts && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span data-testid="brief-notice-type" className={`text-[11px] font-semibold px-2 py-1 rounded ${brief.facts.biddable ? "bg-blue-600 text-white" : "bg-amber-500 text-white"}`}>{brief.facts.notice_type}</span>
                        <span data-testid="brief-reco" className={`text-[11px] font-bold px-2 py-1 rounded ${brief.facts.recommendation === "GO" ? "bg-emerald-600 text-white" : brief.facts.recommendation === "NO-GO" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{brief.facts.recommendation}</span>
                      </div>
                      <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-xs">
                        {[["Agency", brief.facts.agency], ["Place of performance", brief.facts.place_of_performance], ["Set-aside", brief.facts.set_aside], ["Deadline", brief.facts.deadline]].map(([k, v]) => (
                          <React.Fragment key={k}><dt className="text-zinc-400">{k}</dt><dd className="col-span-2 text-zinc-800">{v || "—"}</dd></React.Fragment>
                        ))}
                      </dl>
                      {brief.facts.showstoppers?.length > 0 && (
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 mb-1">Why it's a {brief.facts.recommendation}</div>
                          <ul className="list-disc pl-4 space-y-1 text-xs text-zinc-700" data-testid="brief-showstoppers">
                            {brief.facts.showstoppers.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1">Attached</div>
                          {brief.facts.attached?.length ? (
                            <ul className="space-y-0.5 text-xs text-zinc-700" data-testid="brief-attached">{brief.facts.attached.map((a, i) => <li key={i} className="truncate" title={a}>• {a}</li>)}</ul>
                          ) : <div className="text-xs text-zinc-400 italic">No readable attachments were posted on the notice.</div>}
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1">Missing to bid</div>
                          {brief.facts.missing_to_bid?.length ? (
                            <ul className="space-y-0.5 text-xs text-zinc-700" data-testid="brief-missing">{brief.facts.missing_to_bid.map((a, i) => <li key={i}>• {a}</li>)}</ul>
                          ) : <div className="text-xs text-zinc-400 italic">—</div>}
                        </div>
                      </div>
                    </div>
                  )}
                  {brief.text && <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap border-t border-zinc-100 pt-3" data-testid="brief-text">{brief.text}</p>}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <button data-testid="brief-decline" onClick={() => setBrief(null)} disabled={brief.accepting}
                className="text-sm px-4 py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-700 disabled:opacity-50">Close</button>
              <button data-testid="brief-pass" onClick={passFromBrief} disabled={brief.loading || brief.accepting}
                className="flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 disabled:opacity-50">
                <X className="h-4 w-4" /> Pass — log no-bid
              </button>
              <button data-testid="brief-accept" onClick={() => pursue()} disabled={brief.loading || brief.accepting}
                className="flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50">
                {brief.accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Pursue & pull docs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
